import { useState, useEffect, useRef } from 'react';
import Toast from '../../../../core/components/core/Util/Toast/Toast';
import Fetch from '../../../../core/util/core/Fetch';
import config from '../../../config/config';

/**
 * Hook reutilizável para gerenciar o estado e lógica de importação.
 * Encapsula: fetch de tipos, status, reports, polling, seleção e disparo.
 *
 * @param {Object}  options
 * @param {string}  options.baseEndpoint    - Ex: 'gci/imports'
 * @param {Array}   options.mandatoryTypes  - Ex: ['obras']
 * @returns {Object} Estado e handlers da importação
 */
const useImport = ({ baseEndpoint, mandatoryTypes = [] }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasProcessing, setHasProcessing] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const [currentImport, setCurrentImport] = useState(null);
  const [reports, setReports] = useState([]);
  const [availableTypes, setAvailableTypes] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const pollingRef = useRef(null);

  const endpointBackend = config.endpoints.backend;
  const fullEndpoint = endpointBackend + baseEndpoint;

  // Busca os tipos disponíveis no backend
  const fetchAvailableTypes = async () => {
    try {
      const response = await Fetch.get(fullEndpoint + '/available-types');
      if (response && response.availableTypes) {
        setAvailableTypes(response.availableTypes);
        // Inicializa com todos selecionados (guarda os values)
        setSelectedTypes(response.availableTypes.map(t => t.value));
      }
    } catch (error) {
      Toast.error('Não foi possível buscar os tipos de importação disponíveis.');
    }
  };

  // Busca o status atual (se há importação em andamento)
  const fetchStatus = async () => {
    try {
      const response = await Fetch.get(fullEndpoint + '/status');
      if (response) {
        setHasProcessing(response.hasProcessing);
        setCurrentImport(response.currentImport || null);
        // Quando o backend confirma que não há mais processamento, libera o botão
        if (!response.hasProcessing) {
          setIsPosting(false);
        }
      }
    } catch (error) {
      console.log('Erro ao buscar status:', error);
    }
  };

  // Busca histórico de importações
  const fetchReports = async () => {
    try {
      const response = await Fetch.get(fullEndpoint + '/reports?limit=5');
      if (response) {
        setReports(Array.isArray(response) ? response : []);
      }
    } catch (error) {
      console.log('Erro ao buscar relatórios:', error);
    }
  };

  const fetchAll = async () => {
    await Promise.all([fetchStatus(), fetchReports()]);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchAvailableTypes();
    fetchAll();
  }, []);

  // Polling: enquanto há importação em andamento, atualiza a cada 5s
  useEffect(() => {
    if (hasProcessing || isPosting) {
      pollingRef.current = setInterval(() => {
        fetchAll();
      }, 5000);
    } else {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    }
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [hasProcessing, isPosting]);

  // Toggle de um tipo (não permite desmarcar obrigatórios)
  const handleToggleType = (value) => {
    if (mandatoryTypes.includes(value)) return;
    setSelectedTypes((prev) =>
      prev.includes(value) ? prev.filter((t) => t !== value) : [...prev, value]
    );
  };

  // Selecionar todos
  const handleSelectAll = () => {
    setSelectedTypes(availableTypes.map(t => t.value));
  };

  // Desselecionar opcionais (mantendo obrigatórios)
  const handleDeselectOptionals = () => {
    setSelectedTypes(mandatoryTypes.filter(t => availableTypes.find(a => a.value === t)));
  };

  // Abre o modal de confirmação
  const handleOpenConfirmModal = () => {
    if (hasProcessing || isPosting) return;

    if (selectedTypes.length === 0) {
      Toast.error('Selecione ao menos um tipo de importação.');
      return;
    }

    setShowConfirmModal(true);
  };

  // Confirma e dispara a importação
  const handleConfirmImport = async () => {
    setShowConfirmModal(false);
    await handleImport();
  };

  // Dispara importação
  const handleImport = async () => {
    if (hasProcessing || isPosting) return;

    if (selectedTypes.length === 0) {
      Toast.error('Selecione ao menos um tipo de importação.');
      return;
    }

    try {
      setIsPosting(true);
      const response = await Fetch.post(fullEndpoint + '/start', {
        types: selectedTypes,
      });

      if (response && response.status === 1) {
        Toast.success('Importação disparada com sucesso!');
        fetchAll();
      } else {
        Toast.error(response?.message || 'Não foi possível iniciar a importação.');
        setIsPosting(false);
      }
    } catch (error) {
      Toast.error(
        error.response?.data?.message || error.message || 'Erro desconhecido ao disparar importação'
      );
      setIsPosting(false);
    }
  };

  // Converte value → label
  const getTypeLabel = (value) => {
    const found = availableTypes.find(t => t.value === value);
    return found ? found.label : value;
  };

  const isButtonDisabled = hasProcessing || isPosting;

  return {
    // Estado
    isLoading,
    hasProcessing,
    isPosting,
    isButtonDisabled,
    currentImport,
    reports,
    availableTypes,
    selectedTypes,
    showConfirmModal,
    mandatoryTypes,

    // Handlers
    handleToggleType,
    handleSelectAll,
    handleDeselectOptionals,
    handleOpenConfirmModal,
    handleConfirmImport,
    setShowConfirmModal,
    getTypeLabel,
  };
};

export default useImport;
